import { useCallback, useState } from "react";
import type { QueueSummary } from "../hooks/useQueues";

const COLLAPSED_LS_KEY = "capture.queue.collapsed";

// error → danger tone + "Needs attention"; complete → success tone + "Complete".
const TERMINAL_META: Record<string, { title: string; tone: "danger" | "success"; sectionKey: string }> = {
  error: { title: "Needs attention", tone: "danger", sectionKey: "__error" },
  complete: { title: "Complete", tone: "success", sectionKey: "__complete" },
};

function readCollapsed(key: string): boolean {
  try {
    const raw = localStorage.getItem(COLLAPSED_LS_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(arr) && arr.includes(key);
  } catch {
    return false;
  }
}

function persistCollapsed(key: string, collapsed: boolean): void {
  try {
    const raw = localStorage.getItem(COLLAPSED_LS_KEY);
    const arr = raw ? (JSON.parse(raw) as string[]) : [];
    const set = new Set(Array.isArray(arr) ? arr : []);
    if (collapsed) set.add(key);
    else set.delete(key);
    localStorage.setItem(COLLAPSED_LS_KEY, JSON.stringify([...set]));
  } catch {
    /* best-effort */
  }
}

interface TerminalLaneSectionProps {
  summary: QueueSummary;
  /** Row click → Batches filtered by (stepType, jobTypeId). */
  onNavigateToBatches?: (stepType: string, jobTypeId: string) => void;
}

export function TerminalLaneSection({ summary, onNavigateToBatches }: TerminalLaneSectionProps) {
  const meta = TERMINAL_META[summary.stepType];
  const [collapsed, setCollapsed] = useState(() => (meta ? readCollapsed(meta.sectionKey) : false));

  const toggle = useCallback(() => {
    if (!meta) return;
    setCollapsed((c) => {
      persistCollapsed(meta.sectionKey, !c);
      return !c;
    });
  }, [meta]);

  if (!meta || summary.byJobType.length === 0) return null;

  return (
    <section className="queue-terminal" data-accent={meta.tone} data-testid={`terminal-${summary.stepType}`}>
      <button className="queue-terminal-header" onClick={toggle} aria-expanded={!collapsed}>
        <span className="queue-terminal-title">{meta.title}</span>
        <span className="queue-terminal-total" data-testid={`terminal-total-${summary.stepType}`}>
          {summary.totalCount} docs
        </span>
      </button>
      {!collapsed && (
        <div className="queue-terminal-rows">
          {summary.byJobType.map((lane) => (
            <button
              key={lane.jobTypeId}
              className="queue-terminal-row"
              onClick={() => onNavigateToBatches?.(summary.stepType, lane.jobTypeId)}
            >
              <span className="queue-terminal-jobname">{lane.jobTypeName}</span>
              <span className="queue-terminal-metric">{lane.docs} docs</span>
              <span className="queue-terminal-metric">{lane.batches} batches</span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
